import { Component, OnDestroy, OnInit } from '@angular/core';
import { ToolRequest } from "@models/toolRequests.model";
import { TableConfig } from "@app/shared/util/table-config.utils";
import { Log } from "ng2-logger";
import { ToolRequestsService } from "@services/tool-requests.service";
import { ToastsManager } from "@app/shared/util/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { NotificationService } from "@services/notification.service";
import { Subscription } from "rxjs";
import { ToolRequestStatus } from "@app/tool/model/enums/tool-request-status.enum";
import { Section } from "@models/enums/section.utils";
import { AppUtils } from '@app/shared/util/app.utils';
import { ResolutionService } from "@services/resolution.service";

@Component({
    selector: 'app-tool-request-lines',
    templateUrl: './tool-request-lines.component.html',
    styleUrls: ['./tool-request-lines.component.scss']
})
export class ToolRequestLinesComponent implements OnInit, OnDestroy {

    basePath = 'tool-requests-lines';
    _logger = Log.create(ToolRequestLinesComponent.name);

    pendingToolRequests: ToolRequest[] = [];
    tableConfig = new TableConfig();
    isLoading = false;

    filterStatusList = [];

    newToolRequestsSubscription: Subscription;
    selectedPlantsChangedSubscription: Subscription;

    isMobile = false;

    constructor(private toolRequestsService: ToolRequestsService,
                private toastr: ToastsManager,
                private router: Router,
                private toolRequestService: ToolRequestsService,
                private notificationService: NotificationService,
                private translateService: TranslateService,
                private resolutionService: ResolutionService
    ) {
        this.filterStatusList = this.buildStatusFilterColumnKeys();
        this.translateService.onLangChange.subscribe((event) => {
            this.filterStatusList = this.buildStatusFilterColumnKeys();
        });
        this.selectedPlantsChangedSubscription = this.notificationService.selectedPlantsChangedAsObservable.subscribe(data => {
            this.getPendingToolRequests(this.tableConfig);
        });
    }

    ngOnInit() {
        this.getPendingToolRequests(this.tableConfig);
        this.newToolRequestsSubscription = this.notificationService.linesToolRequestsQueueObservable.subscribe((value) => {
            if (value) {
                this._logger.info("ngOnInit:: linesToolRequestQueueSubscription got value", value);
                this.getPendingToolRequests(this.tableConfig);
            }
        });
    }

    ngOnDestroy(): void {
        if (this.newToolRequestsSubscription) {
            this.newToolRequestsSubscription.unsubscribe();
        }
        if (this.selectedPlantsChangedSubscription) {
            this.selectedPlantsChangedSubscription.unsubscribe();
        }
    }

    getPendingToolRequests(tableConfig = this.tableConfig) {
        this._logger.info('getPendingToolRequests:: Retrieving pending tool returns ');
        this.isLoading = true;
        tableConfig = AppUtils.appendPlantsQuery(tableConfig);
        this.toolRequestsService.getAllToolRequests(tableConfig, 'LINE', Section.ApproveToolRequests).subscribe(
            (data) => {
                this._logger.info('getPendingToolRequests:: API Returned data', data);
                this.pendingToolRequests = data.items;
                this.tableConfig.pager.count = data.total;
                this.isLoading = false;
            },
            (error) => {
                this._logger.error('getPendingToolRequests:: API Returned error', error);
                this.isLoading = false;
            }
        )
    }

    onSortChange(tableState) {
        this.getPendingToolRequests(tableState);
    }

    onPageChange(tableState) {
        this.getPendingToolRequests(tableState);
    }

    onFilter(tableConfig: TableConfig) {
        this.getPendingToolRequests(tableConfig);
    }

    onProcess(toolRequest: ToolRequest) {
        this._logger.info('onProcess:: Opening process page for toolRequest ', toolRequest);
        this.startApproval(toolRequest.id, toolRequest.name);
    }

    onResume(toolRequest: ToolRequest) {
        this._logger.info('onResume:: Opening process page for toolRequest ', toolRequest);
        this.router.navigate([`${this.basePath}/${toolRequest.id}`]);
    }

    startApproval(id: number, toolRequestName: string = '') {
        this._logger.info("startApproval:: Starting approval process for tool request id", id);
        this.toolRequestService.startApprovalById(id).subscribe(
            (data) => {
                this._logger.info('startApproval:: Tool Request processing started', data);
                this.router.navigate([`${this.basePath}/${id}`]);
            },
            (error) => {
                this._logger.error('startApproval:: API Returned error', error);
                this.toastr.error(this.buildCannotStartProcessErrorMessage(toolRequestName));
                this.router.navigate([`${this.basePath}/${id}`]);
            }
        );
    }

    buildCannotStartProcessErrorMessage(toolRequestName) {
        return this.translateService.instant('atom.tool-requests-lines.toasts.cannotStartProcess', {toolRequestName: toolRequestName});
    }

    buildStatusFilterColumnKeys() {
        return [
            {name: this.translateService.instant("generic.statuses.all"), value: 'All'},
            {
                name: this.translateService.instant("generic.statuses.new"),
                value: ToolRequestStatus.NEW
            },
            {
                name: this.translateService.instant("generic.statuses.waitingForApproval"),
                value: ToolRequestStatus.WAITING_FOR_APPROVAL
            },
            {
                name: this.translateService.instant("generic.statuses.canceled"),
                value: ToolRequestStatus.CANCELED
            },
        ];
    }
}
